import { getProductPrice, listValidatedInvoices } from "./pharmacy-store";
import type { ValidatedInvoice } from "./types";

export interface SalesDayPoint {
  label: string;
  date: string;
  revenue: number;
  invoices: number;
}

export interface SalesMonthPoint {
  label: string;
  month: string;
  revenue: number;
}

export interface TopProductPoint {
  name: string;
  revenue: number;
  quantity: number;
}

export interface DashboardAnalytics {
  salesLast7Days: SalesDayPoint[];
  salesLast6Months: SalesMonthPoint[];
  topProducts: TopProductPoint[];
  averageTicket: number;
  revenueWeek: number;
  revenueMonth: number;
  growthWeekPct: number | null;
}

const MONTH_LABELS = [
  "Jan",
  "Fév",
  "Mar",
  "Avr",
  "Mai",
  "Juin",
  "Juil",
  "Aoû",
  "Sep",
  "Oct",
  "Nov",
  "Déc",
];

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function formatDayLabel(d: Date): string {
  return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" });
}

export function buildDashboardAnalytics(
  invoices: ValidatedInvoice[] = listValidatedInvoices(),
): DashboardAnalytics {
  const now = startOfDay(new Date());

  const salesLast7Days: SalesDayPoint[] = [];
  for (let i = 6; i >= 0; i -= 1) {
    const day = new Date(now);
    day.setDate(day.getDate() - i);
    const key = day.toDateString();
    const dayInvoices = invoices.filter(
      (inv) => new Date(inv.validatedAt).toDateString() === key,
    );
    salesLast7Days.push({
      label: formatDayLabel(day),
      date: key,
      revenue: dayInvoices.reduce((s, inv) => s + inv.subtotal, 0),
      invoices: dayInvoices.length,
    });
  }

  const salesLast6Months: SalesMonthPoint[] = [];
  for (let i = 5; i >= 0; i -= 1) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = monthDate.getFullYear();
    const m = monthDate.getMonth();
    const monthInvoices = invoices.filter((inv) => {
      const d = new Date(inv.validatedAt);
      return d.getFullYear() === y && d.getMonth() === m;
    });
    salesLast6Months.push({
      label: `${MONTH_LABELS[m]} ${String(y).slice(-2)}`,
      month: `${y}-${m + 1}`,
      revenue: monthInvoices.reduce((s, inv) => s + inv.subtotal, 0),
    });
  }

  const productMap = new Map<string, TopProductPoint>();
  for (const inv of invoices) {
    for (const line of inv.lines) {
      const key = line.productId;
      const existing = productMap.get(key);
      const rev = line.unitPrice * line.quantity;
      if (existing) {
        existing.revenue += rev;
        existing.quantity += line.quantity;
      } else {
        productMap.set(key, {
          name: line.label,
          revenue: rev,
          quantity: line.quantity,
        });
      }
    }
  }
  const topProducts = [...productMap.values()]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const revenueTotal = invoices.reduce((s, inv) => s + inv.subtotal, 0);
  const averageTicket = invoices.length ? revenueTotal / invoices.length : 0;

  const revenueWeek = salesLast7Days.reduce((s, d) => s + d.revenue, 0);
  const thisWeek = salesLast7Days.slice(-7).reduce((s, d) => s + d.revenue, 0);
  const prevWeekStart = new Date(now);
  prevWeekStart.setDate(prevWeekStart.getDate() - 13);
  const prevWeekEnd = new Date(now);
  prevWeekEnd.setDate(prevWeekEnd.getDate() - 7);
  const prevWeekRevenue = invoices
    .filter((inv) => {
      const d = startOfDay(new Date(inv.validatedAt));
      return d >= prevWeekStart && d < prevWeekEnd;
    })
    .reduce((s, inv) => s + inv.subtotal, 0);
  const growthWeekPct =
    prevWeekRevenue > 0
      ? Math.round(((thisWeek - prevWeekRevenue) / prevWeekRevenue) * 100)
      : null;

  const revenueMonth = salesLast6Months.at(-1)?.revenue ?? 0;

  return {
    salesLast7Days,
    salesLast6Months,
    topProducts,
    averageTicket,
    revenueWeek,
    revenueMonth,
    growthWeekPct,
  };
}

export interface StockValuation {
  totalValue: number;
  pricedSkuCount: number;
  totalUnits: number;
  loading: boolean;
  offline: boolean;
}

export interface LowStockAlert {
  productId: string;
  label: string;
  sku: string;
  quantity: number;
  minThreshold: number;
}

export function computeLowStockAlerts(
  products: Array<{ id: string; label: string; sku: string; status: string; minThreshold: number }>,
  balances: Array<{ productId: string; quantityOnHand: number }>,
  globalMinThreshold = 0,
): LowStockAlert[] {
  const qtyByProduct = new Map<string, number>();
  for (const row of balances) {
    qtyByProduct.set(
      row.productId,
      (qtyByProduct.get(row.productId) ?? 0) + row.quantityOnHand,
    );
  }

  const resolveThreshold = (articleMin: number) =>
    articleMin > 0 ? articleMin : globalMinThreshold > 0 ? globalMinThreshold : 0;

  const alerts: LowStockAlert[] = [];
  for (const product of products) {
    if (product.status !== "ACTIVE") continue;
    const threshold = resolveThreshold(product.minThreshold);
    if (threshold <= 0) continue;
    const quantity = qtyByProduct.get(product.id) ?? 0;
    if (quantity < threshold) {
      alerts.push({
        productId: product.id,
        label: product.label,
        sku: product.sku,
        quantity,
        minThreshold: threshold,
      });
    }
  }

  return alerts.sort((a, b) => a.quantity - b.quantity);
}

export function estimateStockValue(
  balances: Array<{ productId: string; quantityOnHand: number }>,
): Omit<StockValuation, "loading" | "offline"> {
  let totalValue = 0;
  let pricedSkuCount = 0;
  let totalUnits = 0;

  for (const row of balances) {
    const qty = row.quantityOnHand;
    if (qty <= 0) continue;
    totalUnits += qty;
    const price = getProductPrice(row.productId);
    if (price !== null && price > 0) {
      totalValue += qty * price;
      pricedSkuCount += 1;
    }
  }

  return { totalValue, pricedSkuCount, totalUnits };
}
